import React, { useState } from 'react';
import Nav from '../../components/Nav';
import Footer from '../../components/Footer';
import { Mail, Phone, MapPin, Send, CheckCircle, RefreshCw } from 'lucide-react';
import { submitContactMessage } from '../../services/api';

const ContactUs = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg('');
    try {
      const res = await submitContactMessage(formData);
      if (res.success) {
        setSubmitted(true);
      } else {
        setErrorMsg(res.message || 'Failed to submit message. Please try again.');
      }
    } catch (err) {
      console.error("Contact submit error:", err);
      setErrorMsg('Failed to send message. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col pt-14 sm:pt-16">
      <Nav />
      <main className="flex-1 max-w-5xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-5 w-full space-y-3.5 sm:space-y-5">

        {/* Hero Banner */}
        <div className="bg-gradient-to-r from-emerald-800 via-teal-900 to-emerald-900 rounded-xl sm:rounded-2xl p-3.5 sm:py-4 sm:px-6 md:py-4 md:px-6 text-white text-center space-y-1.5 shadow-xs">
          <span className="bg-emerald-500/30 text-emerald-300 border border-emerald-400/40 text-[9px] font-extrabold px-2 py-0.5 rounded-full uppercase tracking-wider">
            We are here to help
          </span>
          <h1 className="text-sm sm:text-lg md:text-xl font-black leading-snug">Contact Our Support Team</h1>
          <p className="text-emerald-100/90 text-[11px] sm:text-xs max-w-xl mx-auto leading-snug">
            Have a question about a course, payment, or enrollment? Reach out to us and our support team will respond promptly.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-5">
          {/* Info cards */}
          <div className="space-y-2.5 sm:space-y-3">
            <div className="bg-white p-3 sm:p-4 rounded-xl border border-gray-100 shadow-2xs flex items-center gap-3">
              <div className="p-2 bg-emerald-50 text-emerald-600 rounded-lg shrink-0">
                <Mail size={18} />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-bold text-gray-900 text-xs sm:text-sm">Email Us</h3>
                <p className="text-[11px] text-gray-500 truncate">support@learninghub.ind.in</p>
              </div>
            </div>

            <div className="bg-white p-3 sm:p-4 rounded-xl border border-gray-100 shadow-2xs flex items-center gap-3">
              <div className="p-2 bg-blue-50 text-blue-600 rounded-lg shrink-0">
                <Phone size={18} />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-bold text-gray-900 text-xs sm:text-sm">Call Support</h3>
                <p className="text-[11px] text-gray-500">7605812086</p>
              </div>
            </div>

            <div className="bg-white p-3 sm:p-4 rounded-xl border border-gray-100 shadow-2xs flex items-center gap-3">
              <div className="p-2 bg-purple-50 text-purple-600 rounded-lg shrink-0">
                <MapPin size={18} />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-bold text-gray-900 text-xs sm:text-sm">Head Office</h3>
                <p className="text-[11px] text-gray-500">4c, Auckland Pl, near Jasal House, Elgin, Kolkata, West Bengal 700017</p>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-2 bg-white p-3.5 sm:p-5 rounded-xl sm:rounded-2xl border border-gray-100 shadow-2xs">
            <h2 className="text-xs sm:text-base font-bold text-gray-900 mb-2.5 sm:mb-3">Send Us a Message</h2>

            {submitted ? (
              <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 p-5 rounded-xl text-center space-y-2">
                <CheckCircle className="w-10 h-10 text-emerald-600 mx-auto" />
                <h3 className="text-sm sm:text-base font-bold">Message Submitted Successfully!</h3>
                <p className="text-xs text-emerald-700 max-w-md mx-auto">
                  Thank you, <strong>{formData.name}</strong>. We have received your inquiry. Our support representative will contact you at <strong>{formData.phone || formData.email}</strong> shortly.
                </p>
                <button
                  onClick={() => { setSubmitted(false); setFormData({ name: '', email: '', phone: '', subject: '', message: '' }); }}
                  className="mt-2 px-4 py-1.5 bg-emerald-600 text-white font-bold text-xs rounded-lg hover:bg-emerald-700 transition-colors"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-2.5 sm:space-y-3">
                {errorMsg && (
                  <div className="p-2.5 bg-red-50 border border-red-200 text-red-700 text-xs font-semibold rounded-lg">
                    {errorMsg}
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3">
                  <div>
                    <label className="block text-[10px] font-bold text-gray-700 uppercase mb-0.5">Your Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none text-xs"
                      placeholder="Full Name"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-gray-700 uppercase mb-0.5">Email Address *</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none text-xs"
                      placeholder="name@example.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3">
                  <div>
                    <label className="block text-[10px] font-bold text-gray-700 uppercase mb-0.5">Mobile Number *</label>
                    <input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none text-xs"
                      placeholder="10-digit mobile number"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-gray-700 uppercase mb-0.5">Subject *</label>
                    <input
                      type="text"
                      required
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none text-xs"
                      placeholder="How can we help?"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-bold text-gray-700 uppercase mb-0.5">Message *</label>
                  <textarea
                    rows={3}
                    required
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none text-xs"
                    placeholder="Write your message here..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full sm:w-auto bg-emerald-600 text-white font-bold px-5 py-2 rounded-lg hover:bg-emerald-700 transition-colors flex items-center justify-center gap-1.5 text-xs shadow-xs cursor-pointer disabled:opacity-60"
                >
                  {loading ? (
                    <>
                      <RefreshCw size={14} className="animate-spin" /> Submitting...
                    </>
                  ) : (
                    <>
                      <Send size={15} /> Send Message
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ContactUs;
